import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ManagementTenant, TenantSettings } from '@xeetr/types/models'
import { useSetAtom } from 'jotai'
import { useContext, useEffect } from 'react'

import { tenantTimezoneAtom } from '../atoms/bookings'
import { ActiveTenantContext } from '../context/common/Context'
import {
  createOptimisticUpdate,
  invalidateTenantQueries,
  rollbackOptimisticUpdate,
} from '../lib/mutationHelpers'
import { queryKeys } from '../lib/queryKeys'
import { SettingsService } from '../services/Settings.service'
import { TenantService } from '../services/Tenant.service'
import { transformToManagementTenant } from '../utils/request/tenantMapper'

import { useErrorHandler } from './common/useErrorHandler'

import { useTenantOptionalService, useTenantRequiredService } from './common/useServiceFactory'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

interface UseTenantReturn {
  tenant: ManagementTenant | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => Promise<void>
  updateTenantSettings: <K extends keyof TenantSettings>(
    key: K,
    value: TenantSettings[K]
  ) => Promise<void>
  updateTenantSettingsBatch: (settings: Partial<TenantSettings>) => Promise<void>
}

export const useTenant = (): UseTenantReturn => {
  const { tenantId, setTenant } = useContext(ActiveTenantContext)
  const queryClient = useQueryClient()
  const setTenantTimezone = useSetAtom(tenantTimezoneAtom)

  const getTenantService = useTenantRequiredService(TenantService)
  const getSettingsService = useTenantOptionalService(SettingsService)

  const tenantQuery = useQuery({
    queryKey: queryKeys.tenant(tenantId || ''),
    queryFn: async () => {
      const service = await getTenantService()
      return service.getTenant()
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  useEffect(() => {
    if (tenantQuery.data?.timezone) {
      setTenantTimezone(tenantQuery.data.timezone)
    }
  }, [tenantQuery.data?.timezone, setTenantTimezone])

  useErrorHandler(
    tenantQuery.isError,
    'Fehler beim Laden der Mandantendaten.',
    'error',
    tenantQuery.error
  )

  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: Partial<TenantSettings>) => {
      if (!tenantId) throw new Error('No tenant ID')
      const service = await getSettingsService()
      const response = await service.patchSettings(settings)
      return transformToManagementTenant(response.tenant)
    },
    onMutate: async (newSettings) => {
      if (!tenantId) return

      return createOptimisticUpdate<ManagementTenant>(
        queryKeys.tenant(tenantId),
        (old) => (old ? { ...old, ...newSettings } : undefined) as ManagementTenant
      )
    },
    onError: (error, _variables, context) => {
      if (!tenantId) return

      rollbackOptimisticUpdate(queryKeys.tenant(tenantId), context)

      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        summary: 'Fehler beim Aktualisieren der Einstellungen',
        detail: 'Die Änderungen konnten nicht gespeichert werden.',
      })

      console.error('Failed to update tenant settings:', error)
    },
    onSuccess: (data) => {
      if (!tenantId) return

      queryClient.setQueryData(queryKeys.tenant(tenantId), data)

      queryClient.invalidateQueries({ queryKey: queryKeys.user() })
      invalidateTenantQueries(tenantId)

      showToast({
        severity: 'success',
        summary: 'Einstellungen aktualisiert',
        detail: 'Die Änderungen wurden erfolgreich gespeichert.',
      })
    },
  })

  useEffect(() => {
    if (tenantQuery.data) {
      setTenant(tenantQuery.data)
    }
  }, [tenantQuery.data, setTenant])

  return {
    tenant: tenantQuery.data,
    isLoading: !tenantId || tenantQuery.isLoading,
    isError: tenantQuery.isError,
    error: tenantQuery.error,
    refetch: async () => {
      await tenantQuery.refetch()
    },
    updateTenantSettings: async <K extends keyof TenantSettings>(
      key: K,
      value: TenantSettings[K]
    ) => {
      await updateSettingsMutation.mutateAsync({ [key]: value } as Partial<TenantSettings>)
    },
    updateTenantSettingsBatch: async (settings: Partial<TenantSettings>) => {
      await updateSettingsMutation.mutateAsync(settings)
    },
  }
}
